import { useEffect, useState } from 'react'
import { Button, Card, CardHeader, CardTitle, CardContent, Alert, Modal, Input, MarkdownEditor } from './index'
import {
  useDocumentTemplateService,
  DocumentTemplate,
  MergeFieldDef,
} from '../services/documentTemplateService'

const MATTER_TYPE_OPTIONS = ['Sale', 'Purchase', 'Transfer', 'Subdivision']

interface EditorState {
  editingId: string | null // null = creating a new template
  name: string
  documentType: string
  partyRolesText: string
  matterTypes: string[] // empty = applies to every matter type
  contentMarkdown: string
  mergeFields: MergeFieldDef[]
}

const blankEditor: EditorState = {
  editingId: null,
  name: '',
  documentType: '',
  partyRolesText: 'Client',
  matterTypes: [],
  contentMarkdown: '# New document\n\nUse {{client.fullName}} and other merge fields here.',
  mergeFields: [],
}

const templateToEditor = (template: DocumentTemplate): EditorState => ({
  editingId: template.id,
  name: template.name,
  documentType: template.documentType,
  partyRolesText: template.partyRoles.join(', '),
  matterTypes: template.matterTypes ?? [],
  contentMarkdown: template.contentMarkdown,
  mergeFields: template.mergeFields,
})

export const TemplateLibrary: React.FC = () => {
  const { listTemplates, createTemplate, updateTemplate, publishTemplate, previewTemplate } = useDocumentTemplateService()

  const [templates, setTemplates] = useState<DocumentTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editor, setEditor] = useState<EditorState>(blankEditor)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const [publishingId, setPublishingId] = useState<string | null>(null)

  const [previewFor, setPreviewFor] = useState<DocumentTemplate | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewUrls, setPreviewUrls] = useState<{ body: string; execution: string } | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await listTemplates()
      setTemplates(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const openCreate = () => {
    setEditor(blankEditor)
    setSaveError(null)
    setEditorOpen(true)
  }

  const openEdit = (template: DocumentTemplate) => {
    setEditor(templateToEditor(template))
    setSaveError(null)
    setEditorOpen(true)
  }

  const toggleMatterType = (type: string) => {
    setEditor((prev) => ({
      ...prev,
      matterTypes: prev.matterTypes.includes(type)
        ? prev.matterTypes.filter((t) => t !== type)
        : [...prev.matterTypes, type],
    }))
  }

  const addMergeField = () => {
    setEditor((prev) => ({ ...prev, mergeFields: [...prev.mergeFields, { key: '', label: '' }] }))
  }

  const updateMergeField = (index: number, patch: Partial<MergeFieldDef>) => {
    setEditor((prev) => ({
      ...prev,
      mergeFields: prev.mergeFields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    }))
  }

  const removeMergeField = (index: number) => {
    setEditor((prev) => ({ ...prev, mergeFields: prev.mergeFields.filter((_, i) => i !== index) }))
  }

  const handleSave = async () => {
    const partyRoles = editor.partyRolesText.split(',').map((r) => r.trim()).filter(Boolean)

    if (!editor.name.trim()) {
      setSaveError('Name is required')
      return
    }
    if (!editor.documentType.trim()) {
      setSaveError('Document type is required')
      return
    }
    if (partyRoles.length === 0) {
      setSaveError('At least one party role is required (e.g. "Client" or "Vendor, Purchaser")')
      return
    }
    if (!editor.contentMarkdown.trim()) {
      setSaveError('Content is required')
      return
    }

    setSaving(true)
    setSaveError(null)
    try {
      if (editor.editingId) {
        await updateTemplate(editor.editingId, {
          name: editor.name.trim(),
          contentMarkdown: editor.contentMarkdown,
          mergeFields: editor.mergeFields.filter((f) => f.key.trim() && f.label.trim()),
          partyRoles,
        })
        setMessage('Template updated. It reverted to draft — publish it to make the changes live.')
      } else {
        await createTemplate({
          name: editor.name.trim(),
          documentType: editor.documentType.trim(),
          matterTypes: editor.matterTypes.length > 0 ? editor.matterTypes : null,
          partyRoles,
          contentMarkdown: editor.contentMarkdown,
          mergeFields: editor.mergeFields.filter((f) => f.key.trim() && f.label.trim()),
        })
        setMessage('Template created as a draft. Publish it once you\'re happy with it.')
      }
      setEditorOpen(false)
      await load()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async (templateId: string) => {
    setPublishingId(templateId)
    setError(null)
    try {
      await publishTemplate(templateId)
      setMessage('Template published.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish template')
    } finally {
      setPublishingId(null)
    }
  }

  const handlePreview = async (template: DocumentTemplate) => {
    setPreviewFor(template)
    setPreviewUrls(null)
    setPreviewError(null)
    setPreviewLoading(true)
    try {
      const result = await previewTemplate(template.id)
      setPreviewUrls({
        body: `data:application/pdf;base64,${result.bodyPdfBase64}`,
        execution: `data:application/pdf;base64,${result.executionPdfBase64}`,
      })
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to render preview')
    } finally {
      setPreviewLoading(false)
    }
  }

  return (
    <div>
      {message && (
        <Alert type="success" className="mb-4">
          {message}
        </Alert>
      )}
      {error && (
        <Alert type="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Card>
        <CardHeader className="flex items-center justify-between flex-row">
          <CardTitle>Document Templates</CardTitle>
          <Button onClick={openCreate}>New Template</Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Built-in templates (marked "System") are shared starting points — editing one creates your own
            customized copy without affecting other firms. Any edit reverts a template to draft until
            you publish it again.
          </p>

          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : templates.length === 0 ? (
            <p className="text-sm text-gray-500">No templates yet.</p>
          ) : (
            <div className="divide-y divide-gray-200">
              {templates.map((template) => (
                <div key={template.id} className="py-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{template.name}</span>
                      {template.tenantId === 'SYSTEM' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">System</span>
                      )}
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          template.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {template.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {template.documentType} &middot; {template.partyRoles.join(', ')}
                      {template.matterTypes && template.matterTypes.length > 0 && (
                        <> &middot; {template.matterTypes.join(', ')}</>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => handlePreview(template)}>
                      Preview
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openEdit(template)}>
                      Edit
                    </Button>
                    {template.tenantId !== 'SYSTEM' && template.status === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => handlePublish(template.id)}
                        disabled={publishingId === template.id}
                      >
                        {publishingId === template.id ? 'Publishing…' : 'Publish'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editor.editingId ? 'Edit Template' : 'New Template'}
        size="lg"
      >
        <div className="space-y-4">
          {saveError && <Alert type="error">{saveError}</Alert>}

          <Input label="Name" value={editor.name} onChange={(e) => setEditor((p) => ({ ...p, name: e.target.value }))} />

          {!editor.editingId && (
            <Input
              label="Document type (slug, e.g. client-authorisation)"
              value={editor.documentType}
              onChange={(e) => setEditor((p) => ({ ...p, documentType: e.target.value }))}
            />
          )}

          <Input
            label="Party roles (comma-separated, e.g. Vendor, Purchaser)"
            value={editor.partyRolesText}
            onChange={(e) => setEditor((p) => ({ ...p, partyRolesText: e.target.value }))}
          />

          {!editor.editingId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Applies to matter types (none = all)</label>
              <div className="flex flex-wrap gap-3">
                {MATTER_TYPE_OPTIONS.map((type) => (
                  <label key={type} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input type="checkbox" checked={editor.matterTypes.includes(type)} onChange={() => toggleMatterType(type)} />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content (Markdown, with {'{{'}merge.fields{'}}'})
            </label>
            <MarkdownEditor
              value={editor.contentMarkdown}
              onChange={(value) => setEditor((p) => ({ ...p, contentMarkdown: value }))}
              mergeFields={editor.mergeFields.filter((f) => f.key.trim())}
              rows={14}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Merge fields reference</label>
              <Button variant="ghost" size="sm" onClick={addMergeField}>Add field</Button>
            </div>
            <div className="space-y-2">
              {editor.mergeFields.map((field, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
                    placeholder="client.fullName"
                    value={field.key}
                    onChange={(e) => updateMergeField(i, { key: e.target.value })}
                  />
                  <input
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    placeholder="Client full name"
                    value={field.label}
                    onChange={(e) => updateMergeField(i, { label: e.target.value })}
                  />
                  <button type="button" className="text-gray-400 hover:text-red-600 text-sm" onClick={() => removeMergeField(i)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save draft'}</Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={previewFor !== null}
        onClose={() => setPreviewFor(null)}
        title={previewFor ? `Preview: ${previewFor.name}` : 'Preview'}
        size="lg"
      >
        {previewLoading && <p className="text-sm text-gray-500">Rendering preview (can take a few seconds)…</p>}
        {previewError && <Alert type="error">{previewError}</Alert>}
        {previewUrls && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Document body (sample data)</p>
              <iframe title="Body preview" src={previewUrls.body} className="w-full h-[50vh] border border-gray-200 rounded" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Execution page</p>
              <iframe title="Execution page preview" src={previewUrls.execution} className="w-full h-[50vh] border border-gray-200 rounded" />
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
